import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, AlertCircle, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// /verify-email-change?token=... — landing page for the email that goes to
// the user's NEW address after they request a change from Edit Account.
// Auto-calls /api/account/change-email/confirm and refreshes the auth
// context on success so the header/profile show the new email everywhere.

export default function VerifyEmailChangeScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const { user, refresh } = useAuth() || {};

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/account/change-email/confirm", { token });
        if (cancelled) return;
        setStatus("ok");
        setMessage(data?.message || "Email updated.");
        try { await refresh?.(); } catch { /* ignore */ }
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err?.response?.data?.detail || "This verification link is invalid or expired.");
      }
    })();
    return () => { cancelled = true; };
  }, [token, refresh]);

  const goHome = () => navigate(user ? "/profile" : "/", { replace: true });

  return (
    <div className="screen fade-in" data-testid="verify-email-change-screen">
      <div className="top-bar">
        <div style={{ width: 36 }} />
        <div className="logo-mark">EMAIL</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ textAlign: "center", paddingTop: 60 }}>
        {status === "loading" && (
          <div data-testid="verify-change-loading">
            <span className="spinner spinner-lg" />
            <p className="body" style={{ color: "var(--text-dim)", marginTop: 22 }}>
              Confirming your new email…
            </p>
          </div>
        )}

        {status === "ok" && (
          <div data-testid="verify-change-success">
            <div
              style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "rgba(232,211,185,0.15)", color: "var(--gold)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <Check size={28} strokeWidth={1.8} />
            </div>
            <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.15 }}>
              Email updated.
            </h2>
            <p className="body" style={{ color: "var(--text-dim)", maxWidth: 320, margin: "0 auto" }}>
              {message}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={goHome}
              data-testid="verify-change-continue-btn"
              style={{ marginTop: 28, width: "100%", justifyContent: "center" }}
            >
              {user ? "Back to profile" : "Sign in"}
            </button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-change-error">
            <div
              style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "rgba(255,120,120,0.14)", color: "#ffb0b0",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <AlertCircle size={28} strokeWidth={1.8} />
            </div>
            <h2 className="h1" style={{ fontSize: 26, marginBottom: 14, lineHeight: 1.15 }}>
              Link didn&apos;t work.
            </h2>
            <p className="body" style={{ color: "var(--text-dim)", maxWidth: 340, margin: "0 auto" }}>
              {message}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={goHome}
              data-testid="verify-change-goto-profile-btn"
              style={{ marginTop: 24, width: "100%", justifyContent: "center", display: "flex", alignItems: "center", gap: 10 }}
            >
              <Mail size={15} strokeWidth={1.8} />
              <span>{user ? "Back to profile" : "Back to sign in"}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
